// Lottery / Scratcher PAYOUT money — faithful port of Android OrderCreateViewModel:
//   setLotteryPayout / setScratcherPayout  → cart lines (isLotteryPayout / isScratcherPayout, amount in
//                                            orderUnitNewPrice, no points)
//   getLoyaltyJson lottery block           → lottery_order_pay / lottery_cash_pay / scratch_order_pay /
//                                            scratch_cash_pay  (how much of each payout was netted against
//                                            the sale vs handed over as cash)
//   calculateOrder lotteryScratcherStatus  → `lottery_scratch` "0".."5"
// `grandTotal` here is Android's mGrandTotal: the order total AFTER the payouts were subtracted (it
// goes negative when the payout exceeds the sale — that is the "cash back to the customer" case).
import { roundHalfUp, toNum } from "./money";

const unitOf = (l) => (toNum(l.orderUnitNewPrice) > 0 ? toNum(l.orderUnitNewPrice) : toNum(l.orderUnitPrice));

/** Σ lottery / scratcher payout lines (Android lotteryPayoutAmount / scratcherPayoutAmount). */
export function lotteryPayoutTotals(cart) {
  let lottery = 0;
  let scratch = 0;
  for (const l of cart || []) {
    if (l.isLotteryPayout) lottery += unitOf(l) * toNum(l.orderCount);
    else if (l.isScratcherPayout) scratch += unitOf(l) * toNum(l.orderCount);
  }
  return { lottery: roundHalfUp(lottery), scratch: roundHalfUp(scratch) };
}

/** Android calculateOrder → lotteryScratcherStatus ("0" none · "5" both · scratcher "2"/"4" · lottery "1"/"3"). */
export function lotteryScratcherStatus(lottery, scratch, grandTotal) {
  const L = toNum(lottery);
  const S = toNum(scratch);
  const G = toNum(grandTotal);
  if (L === 0 && S === 0) return "0";
  if (L !== 0 && S !== 0) return "5";
  if (L === 0) return S >= G ? "2" : "4";
  return L >= G ? "1" : "3";
}

/**
 * Android getLoyaltyJson lottery branch tree — byte-faithful, including its asymmetries. Returns the
 * four split keys plus the two totals; keys Android does not `put` in a branch come back as 0.
 */
export function lotteryPayoutJson({ subtotal = 0, grandTotal = 0, lottery = 0, scratch = 0 } = {}) {
  const sub = toNum(subtotal);
  const G = toNum(grandTotal);
  const L = toNum(lottery);
  const S = toNum(scratch);
  const abs = Math.abs;
  const r = roundHalfUp;
  const out = { lottery_order_pay: 0, lottery_cash_pay: 0, scratch_order_pay: 0, scratch_cash_pay: 0, total_lottery_payout: r(L), total_scratcher_payout: r(S) };
  const hasL = L !== 0;
  const hasS = S !== 0;
  if (hasL && hasS) {
    const T = L + S;
    if (sub === 0) {
      Object.assign(out, { lottery_order_pay: 0, lottery_cash_pay: L, scratch_order_pay: 0, scratch_cash_pay: S });
    } else if (T > G + T) {
      // negative customerOwes — the payouts exceed the sale
      const gPlusT = G + T;
      if (L > S) {
        if (gPlusT > L) Object.assign(out, { lottery_order_pay: L, lottery_cash_pay: 0, scratch_order_pay: r(abs(G + S)), scratch_cash_pay: abs(G) });
        else Object.assign(out, { lottery_order_pay: r(L + S + G), lottery_cash_pay: r(abs(S + G)), scratch_order_pay: 0, scratch_cash_pay: S });
      } else if (L === S) {
        if (gPlusT > L) Object.assign(out, { lottery_order_pay: L, lottery_cash_pay: 0, scratch_order_pay: r(abs(G + S)), scratch_cash_pay: abs(G) });
        else Object.assign(out, { lottery_order_pay: r(T + G), lottery_cash_pay: r(abs(L + G)), scratch_order_pay: 0, scratch_cash_pay: S });
      } else {
        if (gPlusT > L) Object.assign(out, { lottery_order_pay: L, lottery_cash_pay: 0, scratch_order_pay: r(abs(G + S)), scratch_cash_pay: abs(G) });
        else Object.assign(out, { lottery_order_pay: r(G + T), lottery_cash_pay: r(abs(G + S)), scratch_order_pay: 0, scratch_cash_pay: S });
      }
    } else {
      Object.assign(out, { lottery_order_pay: L, lottery_cash_pay: 0, scratch_order_pay: S, scratch_cash_pay: 0 });
    }
  } else if (hasL) {
    if (sub === 0) Object.assign(out, { lottery_order_pay: 0, lottery_cash_pay: abs(G) });
    else if (L < G + L) Object.assign(out, { lottery_order_pay: L, lottery_cash_pay: 0 });
    else Object.assign(out, { lottery_order_pay: r(G + L), lottery_cash_pay: abs(G) });
  } else if (hasS) {
    if (sub === 0) Object.assign(out, { scratch_order_pay: 0, scratch_cash_pay: abs(G) });
    else if (S < G + S) Object.assign(out, { scratch_order_pay: S, scratch_cash_pay: 0 });
    else Object.assign(out, { scratch_order_pay: r(G + S), scratch_cash_pay: abs(G) });
  }
  for (const k of Object.keys(out)) out[k] = r(out[k]);
  return out;
}
